import { useParams } from 'react-router'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Container from '@cloudscape-design/components/container'
import Grid from '@cloudscape-design/components/grid'
import Header from '@cloudscape-design/components/header'
import SpaceBetween from '@cloudscape-design/components/space-between'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import Page from '@/components/common/Page'
import { ChatPanel } from '@/pages/clients/ClientAgent/ChatPanel'
import { useAgentChat } from '@/hooks/useAgentChat'
import styles from './ChatPage.module.css'

function formatRelativeDate(iso: string): string {
  const today = new Date().toISOString().slice(0, 10)
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10)
  const day = iso.slice(0, 10)
  if (day === today) return 'Today'
  if (day === yesterday) return 'Yesterday'
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export default function ChatPage() {
  const { clientId } = useParams<{ clientId: string }>()
  const {
    conversations,
    conversationsLoading,
    messages,
    messagesLoading,
    loading,
    currentConversationId,
    sendMessage,
    startNewChat,
    selectConversation,
  } = useAgentChat(clientId ?? '')

  if (!clientId) return null

  return (
    <Page
      title="Chat"
      helpContent={{
        header: 'Agent Chat',
        body: (
          <div>
            <p>Have a direct conversation with your autonomous marketing agent. Ask questions, request actions, or clarify decisions.</p>
            <h4>How to Use:</h4>
            <ul style={{ marginLeft: 20 }}>
              <li>View conversation history on the left side</li>
              <li>Select or start a new conversation</li>
              <li>Type your message in the compose area and send</li>
              <li>Conversations are organized by date</li>
            </ul>
            <h4>Tips:</h4>
            <p>Be specific with requests for best results. The agent can help clarify campaign settings or execute marketing actions.</p>
          </div>
        ),
      }}
    >
      <Grid
        gridDefinition={[
          { colspan: { default: 12, s: 3 } },
          { colspan: { default: 12, s: 9 } },
        ]}
      >
        {/* ── History panel ───────────────────────────────────── */}
        <Container
          header={<Header variant="h3">History</Header>}
          disableContentPaddings
        >
          <div className={styles.historyPanel}>
            <div className={styles.newChatRow}>
              <Button iconName="add-plus" onClick={startNewChat} fullWidth>
                New chat
              </Button>
            </div>

            <div className={styles.historyScroll}>
              {conversationsLoading ? (
                <Box padding="m" textAlign="center" color="text-body-secondary">
                  <StatusIndicator type="loading">Loading…</StatusIndicator>
                </Box>
              ) : conversations.length === 0 ? (
                <Box padding="m" textAlign="center" color="text-body-secondary">
                  No past conversations.
                </Box>
              ) : (
                <SpaceBetween size="xxxs">
                  {conversations.map((c) => (
                    // No Cloudscape equivalent for a plain selectable list item
                    <button
                      key={c.id}
                      type="button"
                      className={`${styles.historyItem} ${currentConversationId === c.id ? styles.historyItemActive : ''}`}
                      onClick={() => selectConversation(c.id)}
                    >
                      <span className={styles.historyPreview}>{c.preview}</span>
                      <span className={styles.historyDate}>{formatRelativeDate(c.timestamp)}</span>
                    </button>
                  ))}
                </SpaceBetween>
              )}
            </div>
          </div>
        </Container>

        {/* ── Chat panel ──────────────────────────────────────── */}
        <ChatPanel
          messages={messages}
          sendMessage={sendMessage}
          loading={loading}
          historyLoading={messagesLoading}
        />
      </Grid>
    </Page>
  )
}
